import { analytics } from "@heycatch/sdk";

export const CONSENT_KEY = "cl_analytics_consent";
export const CONSENT_EVENT = "cl-consent-changed";

const SENSITIVE_PARAM = /^(session_id|session_token|token|access_token|refresh_token|id_token|code|state|checkout_session_id|email)$/i;
const AUTO_EVENTS = new Set(["$autocapture", "$rageclick", "$dead_click", "$copy_autocapture"]);
const SENSITIVE_PATHS = ["/journal", "/daily"];

export const getConsent = () => {
  try { return localStorage.getItem(CONSENT_KEY); } catch (e) { return null; }
};
export const hasConsent = () => getConsent() === "granted";

export const cleanUrl = (value, isReferrer) => {
  if (typeof value !== "string" || !/^https?:\/\//i.test(value)) return value;
  try {
    const u = new URL(value);
    if (isReferrer && u.origin !== window.location.origin) return `${u.origin}/`;
    u.hash = "";
    [...u.searchParams.keys()].forEach((k) => { if (SENSITIVE_PARAM.test(k)) u.searchParams.delete(k); });
    return u.toString();
  } catch (e) {
    return value.split("#")[0].replace(/([?&])(session_id|token|code)=[^&]*/gi, "$1");
  }
};

const scrub = (obj) => {
  if (!obj || typeof obj !== "object") return;
  Object.keys(obj).forEach((k) => {
    if (/ph_keyword|search_engine/i.test(k)) delete obj[k];
    else if (/url|referrer|href/i.test(k)) obj[k] = cleanUrl(obj[k], /referrer/i.test(k));
    else if (k === "$set" || k === "$set_once") scrub(obj[k]);
  });
};

const pathOf = (url) => {
  try { return new URL(url).pathname; } catch (e) { return window.location.pathname; }
};

// Drops automatic interaction events on sensitive screens and strips secrets from every URL property.
export const sanitizeEvent = (event) => {
  if (!event || event.event === "$$heatmap") return null;
  const p = event.properties || {};
  if (AUTO_EVENTS.has(event.event) && SENSITIVE_PATHS.some((s) => pathOf(p.$current_url).startsWith(s))) return null;
  scrub(p);
  scrub(event.$set);
  scrub(event.$set_once);
  return event;
};

const HEYCATCH_OPT_KEY = "__ph_opt_in_out_phc_oiDt6uXiBiEA2aT43SMzMAFE9D4gMVkRP3BtvYRsmHqe";

// The SDK forgets its opt-in when the identified user changes or resets; restore it from the visitor's choice.
const syncOptIn = () => {
  if (!hasConsent()) return false;
  let stored = false;
  try { stored = localStorage.getItem(HEYCATCH_OPT_KEY) !== null; } catch (e) {}
  if (!stored) analytics.optInCapturing();
  return !stored;
};

export const initAnalytics = () => {
  analytics.init({
    projectKey: "hck_pk_TUQ3seM-0zwSXUqBRjspPHSZivm7bZwy",
    install: { framework: "react", frameworkVersion: "19", agent: "other" },
    optOutCapturingByDefault: true,
    maskAllText: true,
    maskAllElementAttributes: true,
    propertyDenylist: ["email", "name", "question", "interpretation", "reflection", "session_id", "token"],
    beforeSend: sanitizeEvent,
    flushIntervalMs: 250,
  });
  syncOptIn();
};

const notify = () => window.dispatchEvent(new Event(CONSENT_EVENT));

export const grantConsent = () => {
  try { localStorage.setItem(CONSENT_KEY, "granted"); } catch (e) {}
  analytics.optInCapturing();
  notify();
};

export const denyConsent = () => {
  try { localStorage.setItem(CONSENT_KEY, "denied"); } catch (e) {}
  analytics.resetIdentity();
  analytics.optOutCapturing();
  notify();
};

export const track = (name, props) => {
  if (!hasConsent()) return;
  syncOptIn();
  analytics.trackEvent(name, props);
};

export const identify = (userId, plan) => {
  if (!hasConsent() || !userId) return;
  syncOptIn();
  const props = plan ? { plan } : undefined;
  analytics.setIdentity(userId, props);
  if (syncOptIn()) analytics.setIdentity(userId, props);
};

export const resetAnalytics = () => {
  analytics.resetIdentity();
  syncOptIn();
};

// Gives a just-tracked event a moment to send before a full-page redirect.
export const flushBeforeRedirect = () => (hasConsent() ? new Promise((r) => setTimeout(r, 600)) : Promise.resolve());

export const planOf = (entitlements) => (entitlements?.is_premium ? "premium" : "free");
